import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores'
import apiClient from '@/api/client'

const routes: RouteRecordRaw[] = [
  {
    path: '/setup',
    name: 'setup',
    component: () => import('@/views/auth/SetupView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
      },
      {
        path: 'clients',
        name: 'clients',
        component: () => import('@/layouts/SectionLayout.vue'),
        redirect: { name: 'clients.list' },
        children: [
          {
            path: '',
            name: 'clients.list',
            component: () => import('@/views/clients/ClientsView.vue'),
          },
          {
            path: 'new',
            name: 'clients.create',
            component: () => import('@/views/clients/ClientEditView.vue'),
          },
          {
            path: ':clientId',
            name: 'clients.edit',
            component: () => import('@/views/clients/ClientEditView.vue'),
          },
        ],
      },
      {
        path: 'documents',
        name: 'documents',
        component: () => import('@/layouts/SectionLayout.vue'),
        redirect: { name: 'documents.list' },
        children: [
          {
            path: '',
            name: 'documents.list',
            component: () => import('@/views/documents/DocumentsView.vue'),
          },
          {
            path: 'new',
            name: 'documents.create',
            component: () => import('@/views/documents/DocumentEditView.vue'),
          },
          {
            // Spec 037 — before the :documentId param route.
            path: 'record',
            name: 'documents.record',
            component: () => import('@/views/documents/DocumentRecordingView.vue'),
          },
          {
            path: ':documentId',
            name: 'documents.edit',
            component: () => import('@/views/documents/DocumentEditView.vue'),
          },
        ],
      },
      {
        path: 'time',
        name: 'time',
        component: () => import('@/layouts/SectionLayout.vue'),
        redirect: { name: 'time.list' },
        children: [
          {
            path: '',
            name: 'time.list',
            component: () => import('@/views/time/TimeEntriesView.vue'),
          },
        ],
      },
      {
        path: 'tasks',
        name: 'tasks',
        component: () => import('@/layouts/SectionLayout.vue'),
        redirect: { name: 'tasks.list' },
        children: [
          {
            path: '',
            name: 'tasks.list',
            component: () => import('@/views/tasks/TasksView.vue'),
          },
          {
            path: 'new',
            name: 'tasks.create',
            component: () => import('@/views/tasks/TaskEditView.vue'),
          },
          {
            path: ':taskId',
            name: 'tasks.edit',
            component: () => import('@/views/tasks/TaskEditView.vue'),
          },
        ],
      },
      {
        path: 'conversations',
        name: 'conversations',
        component: () => import('@/layouts/SectionLayout.vue'),
        redirect: { name: 'conversations.list' },
        children: [
          {
            path: '',
            name: 'conversations.list',
            component: () => import('@/views/conversations/ConversationsView.vue'),
          },
          {
            path: ':conversationId',
            name: 'conversations.chat',
            component: () => import('@/views/conversations/ConversationView.vue'),
          },
        ],
      },
      {
        path: 'administration',
        name: 'administration',
        component: () => import('@/layouts/SectionLayout.vue'),
        redirect: { name: 'administration.operators' },
        children: [
          {
            path: 'providers',
            name: 'administration.providers',
            redirect: { name: 'administration.providers.list' },
            children: [
              {
                path: '',
                name: 'administration.providers.list',
                component: () => import('@/views/providers/ProvidersView.vue'),
              },
              {
                path: 'new',
                name: 'administration.providers.create',
                component: () => import('@/views/providers/ProviderEditView.vue'),
              },
              {
                path: ':providerId',
                name: 'administration.providers.edit',
                component: () => import('@/views/providers/ProviderEditView.vue'),
              },
            ],
          },
          {
            path: 'document-templates',
            name: 'administration.documentTemplates',
            component: () => import('@/views/documents/DocumentTemplatesView.vue'),
          },
          {
            path: 'document-templates/new',
            name: 'administration.documentTemplates.create',
            component: () => import('@/views/documents/DocumentEditView.vue'),
          },
          {
            path: 'document-templates/:documentId',
            name: 'administration.documentTemplates.edit',
            component: () => import('@/views/documents/DocumentEditView.vue'),
          },
          {
            path: 'audit-logs',
            name: 'administration.auditLogs',
            component: () => import('@/views/administration/AuditLogsView.vue'),
          },
          {
            path: 'audit-logs/:auditLogId',
            name: 'administration.auditLogDetail',
            component: () => import('@/views/administration/AuditLogDetailView.vue'),
          },
          {
            path: 'operators',
            name: 'administration.operators',
            component: () => import('@/views/administration/OperatorsView.vue'),
          },
          {
            path: 'operators/new',
            name: 'administration.operators.create',
            component: () => import('@/views/administration/OperatorEditView.vue'),
          },
          {
            path: 'operators/:operatorId',
            name: 'administration.operators.edit',
            component: () => import('@/views/administration/OperatorEditView.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

async function isAppConfigured(): Promise<boolean> {
  try {
    const data = await apiClient.setupStatusList()
    return data.isConfigured === true
  } catch {
    return true
  }
}

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth !== false)

  if (requiresAuth && !authStore.isAuthenticated) {
    const configured = await isAppConfigured()
    next({ name: configured ? 'login' : 'setup', query: { redirect: to.fullPath } })
  } else if (to.name === 'login' && authStore.isAuthenticated) {
    next({ name: 'dashboard' })
  } else if (to.name === 'setup') {
    const configured = await isAppConfigured()
    if (authStore.isAuthenticated || configured) {
      next({ name: authStore.isAuthenticated ? 'dashboard' : 'login' })
    } else {
      next()
    }
  } else if (requiresAuth && authStore.isAuthenticated) {
    try {
      await authStore.ensureProfile()
      next()
    } catch {
      const configured = await isAppConfigured()
      next({ name: configured ? 'login' : 'setup', query: { redirect: to.fullPath } })
    }
  } else {
    next()
  }
})

export default router
